import { apiGet, apiPatch, apiPut, apiPost, apiDelete, ApiClientError } from './client';

/**
 * Phase 75 (Plan 75-05) — frontend API client for the admin-gated bot-admin
 * surface built in Plan 75-02 (`backend/src/api/schemas/botAdmin.ts`). Field
 * names mirror `botConnectionsListResponse` EXACTLY — fast-json-stringify
 * strips anything not enumerated on the backend, so there is nothing to type
 * here beyond what the backend actually returns. No secret material
 * (PAT/Claude token plaintext) is ever part of this shape (T-75-02-04 /
 * T-75-05-03).
 */

export interface BotAdminConnection {
  connectionId: number;
  slackUserId: string;
  userId: number;
  displayName: string | null;
  email: string | null;
  status: string;
  disabledByAdmin: boolean;
  connectedAt: string | null;
  patStatus: string | null;
  patLastUsedAt: string | null;
  claudeTokenPresent: boolean;
  model: string | null;
}

export interface KillSwitchState {
  enabled: boolean;
}

/**
 * Phase 78 (Plan 78-05 / D-03 gap-closure) — the shared channel-service
 * Claude token status. Boolean-only: mirrors
 * `channelClaudeTokenStatusResponse` (`backend/src/api/schemas/botAdmin.ts`)
 * EXACTLY — the token value itself is never part of this shape.
 */
export interface ChannelClaudeTokenState {
  present: boolean;
  /**
   * Only returned by the set endpoint (store-and-warn): `false` means the token
   * was stored but the best-effort verify call did not confirm it. Absent on the
   * status endpoint, whose shape is present-only.
   */
  verified?: boolean;
}

/**
 * Quick task 260723-e88 — defense-in-depth against an unexpectedly-empty
 * response body. `client.ts`'s `handleResponse` resolves to `undefined` for
 * a genuine 204 / `content-length: 0` response (intentional, untouched
 * here); but a corrupted/truncated body on a 200 (see the backend fix in
 * `botAdminController.ts` for the root cause this guards against) would
 * otherwise crash with a cryptic TypeError reading `.data` off `undefined`,
 * or worse, silently render as a valid empty list. Treat it as an explicit
 * error instead so the existing error banner shows up.
 */
function assertNonEmpty<T extends object>(res: T | undefined, what: string): T {
  if (res === undefined || res === null) {
    throw new ApiClientError({
      statusCode: 502,
      error: 'Empty Response',
      message: `Server returned an empty response${what ? ` (${what})` : ''}`,
    });
  }
  return res;
}

export async function getBotConnections(): Promise<BotAdminConnection[]> {
  const res = await apiGet<{ data: BotAdminConnection[] }>('/admin/bot/connections');
  return assertNonEmpty(res, 'bot connections').data;
}

export async function getKillSwitch(): Promise<KillSwitchState> {
  const res = await apiGet<{ data: KillSwitchState }>('/admin/bot/kill-switch');
  return assertNonEmpty(res, 'kill switch').data;
}

export async function setConnectionDisabled(
  connectionId: number,
  disabled: boolean
): Promise<{ success: boolean }> {
  const res = await apiPatch<{ data: { success: boolean } }>(
    `/admin/bot/connections/${connectionId}/disable`,
    { disabled }
  );
  return res.data;
}

export async function setKillSwitch(enabled: boolean): Promise<KillSwitchState> {
  const res = await apiPatch<{ data: KillSwitchState }>('/admin/bot/kill-switch', { enabled });
  return res.data;
}

/**
 * Phase 78 (Plan 78-05) — present/absent status of the shared
 * channel-service Claude token. Never returns the token value.
 */
export async function getChannelClaudeTokenStatus(): Promise<ChannelClaudeTokenState> {
  const res = await apiGet<{ data: ChannelClaudeTokenState }>('/admin/bot/channel-claude-token/status');
  return assertNonEmpty(res, 'channel Claude token status').data;
}

/**
 * Phase 78 (Plan 78-05) — set/replace the shared channel-service Claude
 * token. Show-once semantics on the caller's side: the input this backs is
 * always empty, never pre-filled with the current value (the backend never
 * returns it in plaintext for this to even be possible).
 */
export async function setChannelClaudeToken(token: string): Promise<ChannelClaudeTokenState> {
  const res = await apiPut<{ data: ChannelClaudeTokenState }>('/admin/bot/channel-claude-token', {
    claudeToken: token,
  });
  return res.data;
}

/**
 * Phase 79 (Plan 79-02 / D-2) — the channel → project binding channel task
 * filing resolves against server-side. Mirrors `channelProjectLinkObject`
 * (`backend/src/api/schemas/botAdmin.ts`) EXACTLY.
 */
export interface ChannelProjectLink {
  id: number;
  channelId: string;
  channelName: string | null;
  projectId: number;
  projectName: string | null;
  projectSlug: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function getChannelProjectLinks(): Promise<ChannelProjectLink[]> {
  const res = await apiGet<{ data: ChannelProjectLink[] }>('/admin/bot/channel-projects');
  return assertNonEmpty(res, 'channel-project links').data;
}

export async function createChannelProjectLink(
  channelId: string,
  projectId: number,
  channelName?: string
): Promise<ChannelProjectLink> {
  const res = await apiPost<{ data: ChannelProjectLink }>('/admin/bot/channel-projects', {
    channelId,
    projectId,
    ...(channelName ? { channelName } : {}),
  });
  return res.data;
}

export async function updateChannelProjectLink(
  id: number,
  projectId: number
): Promise<ChannelProjectLink> {
  const res = await apiPatch<{ data: ChannelProjectLink }>(`/admin/bot/channel-projects/${id}`, {
    projectId,
  });
  return res.data;
}

export async function deleteChannelProjectLink(id: number): Promise<{ success: boolean }> {
  const res = await apiDelete<{ data: { success: boolean } }>(`/admin/bot/channel-projects/${id}`);
  return res.data;
}

/**
 * Phase 112 (Plan 112-01 / PCROUTE-04, Plan 112-04) — the two routing
 * executors. Mirrors `ROUTING_EXECUTORS` (`backend/src/services/
 * taskRoutingService.ts`), the single source of truth for the valid value
 * set (also the DB CHECK constraint from 112-01).
 */
export type RoutingExecutor = 'monka' | 'paperclip';

/** Phase 127 (D-23, D-25): where the SRE agent files its tickets. */
export type SreConfigMode = 'triage' | 'backlog' | 'auto';

/** Phase 118 (Plan 118-02 / PCTRIAGE-08): the triager's per-project mode. */
export type TriagerMode = 'off' | 'propose' | 'auto';

/** Phase 118 (D-118-21): one task holding the triager's WIP slot. */
export interface TriagerWipHolder {
  taskId: number;
  code: string;
  name: string;
  kind: 'robot' | 'proposal';
  paperclipRunStatus: string | null;
  /** Phase 125 (D-17): `shared` or `person:{userId}`. */
  bucketKey: string;
}

/** Phase 125 (D-16, D-17): one WIP bucket — a person's own agent, or the shared builder. */
export interface TriagerWipBucket {
  key: string;
  /** The person's `First Last`, or `Shared builder`. */
  label: string;
  count: number;
  /** `count >= triagerWipLimit`. */
  full: boolean;
}

/**
 * Phase 80 (Plan 80-06 / BOTQ-09) — per-project bot work-queue config.
 * Mirrors `queueConfigObject` (`backend/src/api/schemas/botAdmin.ts`) EXACTLY
 * — fast-json-stringify strips anything not enumerated backend-side.
 */
/**
 * MKS-312 (Plan 126-05 / D-19) — one repository a Factory™ ticket of the project may touch. Mirrors
 * the PUT schema's repository item and the response's `repositories` entries.
 */
export interface ProjectRepository {
  repository: string;
  paperclipProjectId: string;
  description: string | null;
  isMain: boolean;
}

/**
 * MKS-471 (Plan 128-07 / D-15) — provisioning state of one repository linked to the project, as
 * the queue-config list reports it. Mirrors `repositoryProvisioning` in
 * `backend/src/api/schemas/botAdmin.ts`. `none` = no provisioning was ever requested.
 */
export type RepositoryProvisioningStatus = 'pending' | 'created' | 'failed' | 'none';

export interface RepositoryProvisioningEntry {
  repository: string;
  status: RepositoryProvisioningStatus;
  errorCode: string | null;
  requestedAt: string | null;
  attemptCount: number;
  /** The repository is already on the project's allow-list. */
  listed: boolean;
}

export interface ProjectQueueConfig {
  id: number;
  projectId: number;
  projectName: string | null;
  projectSlug: string | null;
  enabled: boolean;
  sourceTaskStateIds: number[];
  /**
   * Quick task 260806-cwr (T-cwr-01): resolved names for `sourceTaskStateIds`
   * above. An id whose TaskState was deleted is simply absent here — diff
   * against `sourceTaskStateIds` to render a "#id (removed)" marker.
   */
  sourceTaskStates: { id: number; name: string }[];
  pollIntervalSeconds: number;
  dailyBudget: number;
  autoMergeOnGreen: boolean;
  /**
   * Retired by Phase 118 (D-118-19): still returned, never read or written
   * (PCTRIAGE-12 drops the column). Optional so no caller depends on it.
   */
  greedyMode?: boolean;
  /**
   * Phase 112 (Plan 112-01 / PCROUTE-04, Plan 112-04): the executor a
   * routing decision resolves to when no advisory is available for the
   * task.
   */
  fallbackExecutor: RoutingExecutor;
  /** Phase 118 (Plan 118-02 / PCTRIAGE-08): triager mode and WIP cap. */
  triagerMode: TriagerMode;
  triagerWipLimit: number;
  /** Phase 123 (PCAPPROVE-06): cap on open feature approvals, and the live open count. */
  triagerApprovalCap: number;
  triagerApprovals: { open: number; cap: number };
  /**
   * Phase 127 (Plan 127-07 / D-24, D-25): the SRE agent's switch, mode and budgets (per tick, per
   * UTC day, per weekly audit run). Optional on the wire type so older fixtures keep compiling;
   * the server returns all five for every config.
   */
  sreEnabled?: boolean;
  sreMode?: SreConfigMode;
  sreTickBudget?: number;
  sreDailyBudget?: number;
  sreAuditBudget?: number;
  /** Phase 118 (D-118-21): live WIP (count + holders) from the assign transaction's predicate. */
  triagerWip: {
    count: number;
    limit: number;
    holders: TriagerWipHolder[];
    /** Phase 125 (D-17): per-bucket reading; the limit applies to each bucket. */
    buckets: TriagerWipBucket[];
  };
  /**
   * Phase 113 (Plan 113-04 / PCSYNC-01): the Factory™ company/project this
   * project's routed-to-paperclip work dispatches into. Null means the
   * mapping is unconfigured — a Factory™-routed task in this project will
   * report a dispatch failure naming the missing mapping. Set by hand
   * through this same form until Phase 115's PCPROV-01/02 provisioning
   * populates it automatically.
   */
  paperclipCompanyId: string | null;
  paperclipProjectId: string | null;
  /**
   * MKS-312 (Plan 126-05): the repository allow-list, main first. Optional on the wire type so
   * legacy fixtures keep compiling; the server emits it for every config.
   */
  repositories?: ProjectRepository[];
  /**
   * MKS-471 (Plan 128-07): provisioning state per linked repository ([] when the config is not
   * bound to a Factory™ company). Optional on the wire type so legacy fixtures keep compiling.
   */
  repositoryProvisioning?: RepositoryProvisioningEntry[];
  dailyBudgetUsedCount: number;
  dailyBudgetResetDate: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Body for PUT /api/admin/bot/project-queue-config/:projectId — a full replace. */
export interface UpsertProjectQueueConfigInput {
  enabled: boolean;
  sourceTaskStateIds: number[];
  pollIntervalSeconds: number;
  dailyBudget: number;
  autoMergeOnGreen: boolean;
  /**
   * Phase 118 (Plan 118-02 / PCTRIAGE-08, D-118-13): OPTIONAL — omitted
   * leaves the triager knobs unchanged server-side. (`greedyMode` was
   * removed from this body by D-118-19; the server still tolerates it.)
   */
  triagerMode?: TriagerMode;
  triagerWipLimit?: number;
  /** Phase 123: sent only while the approval release flag is on; omitted = unchanged server-side. */
  triagerApprovalCap?: number;
  /**
   * Phase 127 (Plan 127-07 / D-24): OPTIONAL — omitted = unchanged server-side. The server
   * refuses `sreMode: 'backlog'` (409 `sre_backlog_gate`) until the accuracy gate opens.
   */
  sreEnabled?: boolean;
  sreMode?: SreConfigMode;
  sreTickBudget?: number;
  sreDailyBudget?: number;
  sreAuditBudget?: number;
  /**
   * Phase 112 (Plan 112-04 / PCROUTE-07): OPTIONAL — omitted leaves the
   * column at its current/default value server-side (never a required
   * seventh field on this "full replace" PUT, per the frozen
   * `botProjectQueueConfig.integration.test.ts` guard).
   */
  fallbackExecutor?: RoutingExecutor;
  /**
   * Phase 113 (Plan 113-04 / PCSYNC-01): OPTIONAL, same reasoning as
   * fallbackExecutor above — omitted leaves the column unchanged
   * server-side. An empty string clears the mapping to NULL.
   */
  paperclipCompanyId?: string | null;
  paperclipProjectId?: string | null;
  /**
   * MKS-312 (Plan 126-05): OPTIONAL — omitted leaves the list unchanged; `[]` clears it. The main
   * row's project becomes the scalar `paperclipProjectId`.
   */
  repositories?: ProjectRepository[];
}

export async function getProjectQueueConfigs(): Promise<ProjectQueueConfig[]> {
  const res = await apiGet<{ data: ProjectQueueConfig[] }>('/admin/bot/project-queue-config');
  return assertNonEmpty(res, 'project queue configs').data;
}

export async function upsertProjectQueueConfig(
  projectId: number,
  input: UpsertProjectQueueConfigInput
): Promise<ProjectQueueConfig> {
  const res = await apiPut<{ data: ProjectQueueConfig }>(
    `/admin/bot/project-queue-config/${projectId}`,
    input
  );
  return res.data;
}

/** MKS-471 (Plan 128-07 / D-14 trigger 4): retry the provisioning of one linked repository. */
export async function retryRepositoryProvisioning(
  projectId: number,
  repository: string
): Promise<RepositoryProvisioningEntry> {
  const res = await apiPost<{ data: RepositoryProvisioningEntry }>(
    `/admin/bot/project-queue-config/${projectId}/repository-provisioning`,
    { repository }
  );
  return res.data;
}

/**
 * Phase 127 (Plan 127-07 / R10; D-26) — the SRE agent's track record for one project. Mirrors
 * `sreStatsResponse` (`backend/src/api/schemas/botAdmin.ts`) EXACTLY.
 */
export interface SreBreakdown {
  filed: number;
  correct: number;
  wrong: number;
  pending: number;
}

export interface SreBacklogReason {
  code: 'age' | 'decided' | 'accuracy';
  message: string;
}

export interface SreStats extends SreBreakdown {
  decided: number;
  /** Whole percentage rounded half-up from correct / decided, or null when nothing is decided. */
  accuracyPercent: number | null;
  firstFiledAt: string | null;
  /** Low-confidence and audit tickets are also counted in the overall figures above. */
  lowConfidence: SreBreakdown;
  audit: SreBreakdown;
  backlogSwitch: { allowed: boolean; reasons: SreBacklogReason[] };
}

export async function getSreStats(projectId: number): Promise<SreStats> {
  const res = await apiGet<{ data: SreStats }>(
    `/admin/bot/project-queue-config/${projectId}/sre`
  );
  return assertNonEmpty(res, 'SRE stats').data;
}

export async function disableProjectQueueConfig(
  projectId: number
): Promise<{ success: boolean }> {
  const res = await apiDelete<{ data: { success: boolean } }>(
    `/admin/bot/project-queue-config/${projectId}`
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Phase 112 (Plan 112-04 / PCROUTE-06, D-112-06) — routing read + audited
// override. Mirrors `routedTaskListResponse` / `routingOverrideResponse`
// (`backend/src/api/schemas/botAdmin.ts`) EXACTLY.
// ---------------------------------------------------------------------------

/**
 * One row per task in a project carrying an open or decided routing
 * request. `source` is `null` for a task whose request is open but not yet
 * decided; `executor` is `null` in that same case.
 */
export interface RoutedTask {
  taskId: number;
  code: string | null;
  name: string;
  executor: RoutingExecutor | null;
  source: 'advisory' | 'fallback' | 'override' | null;
  decidedAt: string | null;
  routingRequestedAt: string;
  /** The D3 "bot is working" in-flight marker — non-null while claimed. */
  botClaimedAt: string | null;
}

export interface OverrideTaskRoutingInput {
  executor: RoutingExecutor;
  reason?: string;
}

export interface OverrideTaskRoutingResult {
  taskId: number;
  executor: RoutingExecutor;
  changed: boolean;
}

export async function getRoutedTasks(projectId: number): Promise<RoutedTask[]> {
  const res = await apiGet<{ data: RoutedTask[] }>(`/admin/bot/routing/${projectId}`);
  return assertNonEmpty(res, 'routed tasks').data;
}

export async function overrideTaskRouting(
  taskId: number,
  input: OverrideTaskRoutingInput
): Promise<OverrideTaskRoutingResult> {
  const res = await apiPost<{ data: OverrideTaskRoutingResult }>(
    `/admin/bot/routing/tasks/${taskId}/override`,
    input
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Phase 115 (Plan 115-04 / PCPROV-01) — the stored, dated Factory™ catalog
// backing the binding picker. Mirrors `paperclipCatalogResponse` /
// `paperclipCatalogRefreshResponse` (`backend/src/api/schemas/botAdmin.ts`)
// EXACTLY.
// ---------------------------------------------------------------------------

export interface PaperclipCatalogCompany {
  id: string;
  name: string;
}

export interface PaperclipCatalogProject {
  id: string;
  name: string;
  companyId: string;
}

/**
 * NEVER the result of a blocking network call from the admin's own
 * perspective: this is a stored snapshot, dated by `fetchedAt`, with `stale`
 * telling the picker whether to nudge the admin toward a refresh (D-115-03).
 */
export interface PaperclipCatalog {
  fetchedAt: string | null;
  companies: PaperclipCatalogCompany[];
  projects: PaperclipCatalogProject[];
  truncated: boolean;
  stale: boolean;
}

export interface RefreshPaperclipCatalogResult {
  requestId: string;
}

export async function getPaperclipCatalog(): Promise<PaperclipCatalog> {
  const res = await apiGet<{ data: PaperclipCatalog }>('/admin/bot/paperclip-catalog');
  return assertNonEmpty(res, 'paperclip catalog').data;
}

/**
 * Returns immediately with the request id — the real answer arrives later,
 * asynchronously, over the relay. Callers must NOT assume the catalog has
 * changed on success; schedule a re-read instead (see `useBotAdmin.ts`'s
 * `useRefreshPaperclipCatalog`).
 */
export async function refreshPaperclipCatalog(
  projectId: number,
  companyId?: string
): Promise<RefreshPaperclipCatalogResult> {
  const res = await apiPost<{ data: RefreshPaperclipCatalogResult }>(
    '/admin/bot/paperclip-catalog/refresh',
    { projectId, ...(companyId ? { companyId } : {}) }
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Phase 121 (Plan 121-03 / PCATTR-06, PCATTR-07) — per-person Factory™
// attribution: the admin read model, the on-demand roster refresh and the
// per-row opt-in toggle. Field names mirror `paperclipMembersOverviewResponse`
// in `backend/src/api/schemas/botAdmin.ts` EXACTLY (anything not enumerated
// there is stripped by the backend).
// ---------------------------------------------------------------------------

export type PaperclipMemberState =
  | 'no_match'
  | 'not_member'
  | 'viewer'
  | 'ready_disabled'
  | 'enabled';

export interface PaperclipMemberCompany {
  paperclipCompanyId: string;
  projectId: number;
  projectName: string | null;
  lastRequestedAt: string | null;
  lastSyncedAt: string | null;
  lastFailedAt: string | null;
  lastFailureReason: string | null;
}

export interface PaperclipMemberPerson {
  userId: number;
  displayName: string;
  email: string;
  paperclipCompanyId: string;
  mappingId: number | null;
  paperclipUserId: string | null;
  membershipRole: string | null;
  membershipStatus: string | null;
  syncedAt: string | null;
  attributionEnabled: boolean;
  state: PaperclipMemberState;
  stale: boolean;
  // Phase 122 (D-16) — display-only Claude-token sync state (never a secret).
  tokenSyncStatus: string | null;
  tokenSyncPendingOperation: string | null;
  tokenSyncErrorCode: string | null;
  tokenSyncedAt: string | null;
  tokenSyncRequestedAt: string | null;
  tokenSyncVersion: number | null;
  // Phase 124 (D-11) — provisioning state, display only. The status fields are filled only
  // on the provisioning company's rows and are null elsewhere.
  provisionedByMonksheets: boolean;
  inProvisionScope: boolean;
  provisionStatus: PaperclipProvisionStatus | null;
  provisionErrorCode: string | null;
  provisionAt: string | null;
  // Phase 125 (D-17) — the person's own builder agent, display only. The name is the stored
  // agent name; every field is null when the row has no agent.
  personAgentId: string | null;
  personAgentName: string | null;
  personAgentStatus: PaperclipPersonAgentStatus | null;
  personAgentPendingOperation: string | null;
  personAgentErrorCode: string | null;
  personAgentSyncedAt: string | null;
  personAgentRequestedAt: string | null;
}

export type PaperclipPersonAgentStatus = 'active' | 'paused' | 'failed' | 'absent';

/** Phase 125 (D-17) — the last company re-sync, from the company's audit rows. */
export interface PaperclipPersonAgentLastResync {
  companyId: string;
  requestedAt: string | null;
  resultAt: string | null;
  checkedCount: number | null;
  updatedCount: number | null;
  skippedBusyCount: number | null;
  failedCount: number | null;
  errorCode: string | null;
}

/** Phase 125 (D-13, D-17) — the person-agent switch, the read-only pilot list, last re-syncs. */
export interface PaperclipPersonAgentsOverview {
  switchOn: boolean;
  pilotUserIds: number[];
  lastResync: PaperclipPersonAgentLastResync[];
}

export type PaperclipProvisionStatus =
  | 'requested'
  | 'provisioned'
  | 'archived'
  | 'refused'
  | 'failed';

/** Phase 122 (D-16) — the token-sync writer gate; `available` is false on staging. */
export interface PaperclipTokenSyncGate {
  available: boolean;
  switchOn: boolean;
  companyId: string | null;
}

/**
 * Phase 124 (D-11, D-13) — the provisioning gate and counts. `available` is false outside
 * production with the env company; `companyId` is the one company provisioning may target.
 */
export interface PaperclipProvisionGate {
  available: boolean;
  switchOn: boolean;
  companyId: string | null;
  inScope: number;
  missing: number;
  byEmployeeType: { name: string; count: number }[];
}

/**
 * MKS-275 — the TopMonks Robot's token sync, one row per company the Robot gate
 * names. `available` is false in an environment whose task definition names no
 * company; `tokenPresent` is false until an admin has set a token at all. Display
 * only: there is no value and no fingerprint here, ever.
 */
export interface PaperclipRobotTokenSyncCompany {
  paperclipCompanyId: string;
  status: string | null;
  pendingOperation: string | null;
  errorCode: string | null;
  version: number | null;
  syncedAt: string | null;
  requestedAt: string | null;
}

export interface PaperclipRobotTokenSync {
  available: boolean;
  switchOn: boolean;
  tokenPresent: boolean;
  companies: PaperclipRobotTokenSyncCompany[];
}

export interface PaperclipMembersOverview {
  switchOn: boolean;
  staleAfterHours: number;
  companies: PaperclipMemberCompany[];
  people: PaperclipMemberPerson[];
  tokenSync: PaperclipTokenSyncGate;
  robotTokenSync: PaperclipRobotTokenSync;
  provision: PaperclipProvisionGate;
  personAgents: PaperclipPersonAgentsOverview;
}

export interface ProvisionPaperclipMembersResult {
  requested: number;
  skipped: number;
}

/** MKS-275 — how many companies the "Sync now" button asked. Never a token. */
export interface SyncPaperclipRobotTokenResult {
  requested: number;
}

export interface RefreshPaperclipMembersResult {
  requestId: string;
  projectId: number;
}

export async function getPaperclipMembers(): Promise<PaperclipMembersOverview> {
  const res = await apiGet<{ data: PaperclipMembersOverview }>('/admin/bot/paperclip-members');
  return res.data;
}

/**
 * Returns immediately with the request id — the roster arrives later,
 * asynchronously, over the write-back relay. Callers must NOT assume the roster
 * has changed on success; schedule a re-read instead.
 */
export async function refreshPaperclipMembers(
  paperclipCompanyId: string
): Promise<RefreshPaperclipMembersResult> {
  const res = await apiPost<{ data: RefreshPaperclipMembersResult }>(
    '/admin/bot/paperclip-members/refresh',
    { paperclipCompanyId }
  );
  return res.data;
}

/**
 * MKS-275 — ask every bound company to rotate its ROBOT_CLAUDE_TOKEN secret now.
 * One token, so there is no per-company variant: a partial rotation that left the
 * companies disagreeing is not expressible. Returns immediately with a count — the
 * results arrive later, asynchronously, over the write-back relay, so callers must
 * re-read rather than assume the rows moved.
 */
export async function syncPaperclipRobotToken(): Promise<SyncPaperclipRobotTokenResult> {
  const res = await apiPost<{ data: SyncPaperclipRobotTokenResult }>(
    '/admin/bot/paperclip-members/robot-token-sync',
    {}
  );
  return res.data;
}

/** The server is the authority: a 409 means the row is not ready to be enabled. */
export async function setPaperclipMemberAttribution(
  mappingId: number,
  attributionEnabled: boolean
): Promise<PaperclipMemberPerson> {
  const res = await apiPatch<{ data: PaperclipMemberPerson }>(
    `/admin/bot/paperclip-members/${mappingId}`,
    { attributionEnabled }
  );
  return res.data;
}

/**
 * Phase 124 (D-11) — request provisioning for one in-scope person (`userId`) or, without
 * it, for everyone missing. Returns immediately; accounts are created asynchronously by the
 * dispatch job. The server re-checks the company, the gate, the switch and the scope (409).
 */
export async function provisionPaperclipMembers(input: {
  paperclipCompanyId: string;
  userId?: number;
}): Promise<ProvisionPaperclipMembersResult> {
  const res = await apiPost<{ data: ProvisionPaperclipMembersResult }>(
    '/admin/bot/paperclip-members/provision',
    input
  );
  return res.data;
}

/** Phase 125 (D-17) — what an admin Reconcile of person agents reports. */
export interface ReconcilePaperclipPersonAgentsResult {
  requested: number;
  held: number;
  skipped: number;
}

export interface ResyncPaperclipPersonAgentsResult {
  requestId: string;
}

/**
 * Phase 125 (D-12, D-17) — re-run the pause/resume lifecycle for one person (`userId`) or for
 * everyone with an agent in the company. Enqueues jobs only; 409 when the switch is off.
 */
export async function reconcilePaperclipPersonAgents(input: {
  paperclipCompanyId: string;
  userId?: number;
}): Promise<ReconcilePaperclipPersonAgentsResult> {
  const res = await apiPost<{ data: ReconcilePaperclipPersonAgentsResult }>(
    '/admin/bot/paperclip-members/person-agents/reconcile',
    input
  );
  return res.data;
}

/**
 * Phase 125 (D-06, D-17) — request one re-sync of a company's person agents from its template.
 * Returns immediately; the result lands asynchronously and shows as the last re-sync.
 */
export async function resyncPaperclipPersonAgents(
  paperclipCompanyId: string
): Promise<ResyncPaperclipPersonAgentsResult> {
  const res = await apiPost<{ data: ResyncPaperclipPersonAgentsResult }>(
    '/admin/bot/paperclip-members/person-agents/resync',
    { paperclipCompanyId }
  );
  return res.data;
}
